import { useCallback, useMemo, useRef, useState } from "react"
import { Link, useNavigate } from "@tanstack/react-router"
import { format } from "date-fns"
import {
	IconOfficeFill18 as OfficeIcon,
	IconEnvelopePlusFill18 as SubscribeIcon,
	IconEnvelopeMinusFill18 as UnsubscribeIcon,
} from "nucleo-ui-fill-18"
import {
	IconCloudUploadOutline18 as CloudUpload,
	IconPlusOutline18 as PlusIcon,
	IconMagnifierOutline18 as Search,
} from "nucleo-ui-outline-18"
import { useDebounce } from "react-use"

import type { BrandfetchData } from "@productlane/api/src/browser"
import type { Company } from "@productlane/zero"
import { cn } from "@productlane/lib/src/browser"
import { formatRevenue } from "@productlane/lib/src/format-revenue"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import {
	AlertDialog,
	Button,
	Empty,
	IconButton,
	Input,
	NonSortableTableHeader,
	ScrollArea,
	ScrollViewport,
	SortableTableHeader,
	Tooltip,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import EmptyCompany from "@/assets/empty-states/empty-company.png"
import { MetaInfo } from "@/components/meta-info"
import { SelectionCheckbox } from "@/components/selection-checkbox"
import { SelectionPill } from "@/components/selection-pill"
import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { useSetBackNavigation } from "@/lib/back-navigation"
import { captureError } from "@/lib/capture-error"
import {
	rowHighlightClass,
	useListRowSelection,
} from "@/lib/hooks/use-list-row-selection"
import { hasLinearConnection } from "@/lib/linear-connection"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import {
	chunkContactIds,
	SUBSCRIPTION_CHUNK_SIZE,
} from "../contacts/bulk-selection"
import { CustomersPageHeader } from "../customers-tabs"
import { useDefaultCustomerStatus } from "../threads/components/right-bar/customer-properties/default-customer-status"
import { Owner } from "../threads/components/right-bar/customer-properties/icons/owner"
import { CompanyLogo } from "./company-logo"
import { CreateCustomerDialog } from "./create-customer-dialog"
import { CompaniesFilter } from "./filter/companies-filter"
import { CompaniesFilterControl } from "./filter/companies-filter-control"
import { useCompaniesFilterUrlSync } from "./filter/use-filter-url-sync"
import { useCompaniesSortState } from "./sorting/companies-sort-state"
import {
	useCompaniesFilters,
	useCompaniesVirtualizer,
} from "./sorting/use-companies-virtualizer"
import { useCompanyContactCounts } from "./use-company-contact-counts"
import { useSelectAllCompanies } from "./use-select-all-companies"

const CUSTOMER_ROW_SIZE = 49
const PRELOAD_PAGE_SIZE = 100

export const customersPagePreloadQueries = () => [
	queries.companiesWithContacts({
		sortColumn: "name",
		limit: PRELOAD_PAGE_SIZE,
	}),
]

export const CustomersScreen = () => {
	const [isCompanyOpen, setIsCompanyOpen] = useState(false)
	const [search, setSearch] = useState("")
	const [debouncedSearch, setDebouncedSearch] = useState("")
	const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set())
	const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false)
	const [isDeleting, setIsDeleting] = useState(false)
	// Kept apart from the open flag so the dialog copy holds while it closes.
	const [subscriptionTarget, setSubscriptionTarget] = useState(false)
	const [isConfirmSubscriptionOpen, setIsConfirmSubscriptionOpen] =
		useState(false)
	const [pendingSubscription, setPendingSubscription] = useState<
		boolean | null
	>(null)
	const [anchorResetKey, setAnchorResetKey] = useState(0)
	const { sortType, setSortType } = useCompaniesSortState()
	const navigate = useNavigate()
	const zero = useZero()
	const { role } = useRole()
	const filters = useCompaniesFilters()

	useDebounce(() => setDebouncedSearch(search), 400, [search])

	useCompaniesFilterUrlSync()
	useSetBackNavigation({ type: "customers", url: "/customers" })
	const workspace = useZeroWorkspace()
	const defaultStatus = useDefaultCustomerStatus()

	const scrollportRef = useRef<HTMLDivElement | null>(null)
	const virt = useCompaniesVirtualizer({
		scrollElementRef: scrollportRef,
		estimateSize: CUSTOMER_ROW_SIZE,
		search: debouncedSearch,
		anchorResetKey,
	})
	const virtualItems = virt.virtualizer.getVirtualItems()
	const totalCompanies = virt.total ?? virt.estimatedTotal
	const hasCompanies = totalCompanies > 0 || !virt.complete

	// The list is windowed, so only the rows zero-virtual has loaded can be named.
	const loadedIds = useMemo(
		() =>
			virtualItems
				.map((item) => (virt.rowAt(item.index) as Company | undefined)?.id)
				.filter((id): id is string => !!id),
		[virtualItems, virt],
	)
	const selectedCount = selectedIds.size
	const allLoadedSelected =
		loadedIds.length > 0 && loadedIds.every((id) => selectedIds.has(id))

	const toggleAllLoaded = useCallback(() => {
		setSelectedIds((current) =>
			loadedIds.every((id) => current.has(id))
				? new Set()
				: new Set([...current, ...loadedIds]),
		)
	}, [loadedIds])

	const selection = useMemo(
		() => ({ filters, search: debouncedSearch.trim() || undefined }),
		[filters, debouncedSearch],
	)
	const { cancelSelectAll } = useSelectAllCompanies({
		selection,
		loadedIds,
		setSelectedIds,
		enabled: !!role && role !== "VIEWER" && hasCompanies,
	})
	const clearSelection = useCallback(() => {
		cancelSelectAll()
		setSelectedIds(new Set())
	}, [cancelSelectAll])

	const companyIdAt = useCallback(
		(index: number) => (virt.rowAt(index) as Company | undefined)?.id,
		[virt],
	)
	const rows = useListRowSelection({
		count: virt.virtualizer.options.count,
		idAt: companyIdAt,
		selectedIds,
		setSelectedIds,
		onClear: clearSelection,
		canSelect: !!role && role !== "VIEWER",
		scrollToIndex: (index) => virt.virtualizer.scrollToIndex(index),
		openAt: (index) => {
			const id = companyIdAt(index)
			if (id) void navigate({ to: "/customers/$id", params: { id } })
		},
		resetKey: `${sortType}:${JSON.stringify(selection)}`,
	})

	const windowIndices = useMemo(
		() => virtualItems.map((item) => item.index),
		[virtualItems],
	)
	const contactCounts = useCompanyContactCounts(
		windowIndices,
		(index) => (virt.rowAt(index) as Company | undefined)?.id,
	)

	const deleteSelected = useCallback(async () => {
		const count = selectedIds.size
		setIsDeleting(true)
		setAnchorResetKey((key) => key + 1)
		try {
			for (const ids of chunkContactIds([...selectedIds])) {
				await mutateOk(zero.mutate(mutators.companies.deleteMany({ ids })))
			}
			analytics.track("bulk-delete-companies", { count })
			clearSelection()
			setIsConfirmDeleteOpen(false)
			toast.success({
				title: `Deleted ${count} ${count === 1 ? "company" : "companies"}`,
				icon: OfficeIcon,
			})
		} catch (error) {
			captureError(error, {
				domain: "general",
				action: "bulk delete companies",
				extra: { count },
			})
			toast.error({
				title: `Could not delete ${count === 1 ? "this company" : "these companies"}`,
				description: "Nothing was removed. Try again.",
				icon: OfficeIcon,
			})
		} finally {
			setIsDeleting(false)
		}
	}, [selectedIds, zero, clearSelection])

	const openSubscriptionDialog = useCallback((isSubscribed: boolean) => {
		setSubscriptionTarget(isSubscribed)
		setIsConfirmSubscriptionOpen(true)
	}, [])

	const setSelectedSubscribed = useCallback(async () => {
		const isSubscribed = subscriptionTarget
		const count = selectedIds.size
		const companies = count === 1 ? "company" : "companies"
		const icon = isSubscribed ? SubscribeIcon : UnsubscribeIcon
		setPendingSubscription(isSubscribed)
		try {
			// The people sit outside this list's synced rows, so wait for the
			// server to say the update went through.
			await Promise.all(
				chunkContactIds([...selectedIds], SUBSCRIPTION_CHUNK_SIZE).map((ids) =>
					mutateOk(
						zero.mutate(
							mutators.companies.setSubscribedMany({ ids, isSubscribed }),
						),
						"server",
					),
				),
			)
			analytics.track(
				isSubscribed
					? "bulk-subscribe-companies"
					: "bulk-unsubscribe-companies",
				{ count },
			)
			clearSelection()
			setIsConfirmSubscriptionOpen(false)
			toast.success({
				title: `${isSubscribed ? "Subscribed" : "Unsubscribed"} everyone at ${count} ${companies}`,
				icon,
			})
		} catch (error) {
			captureError(error, {
				domain: "general",
				action: isSubscribed
					? "bulk subscribe companies"
					: "bulk unsubscribe companies",
				extra: { count },
			})
			toast.error({
				title: `Could not ${isSubscribed ? "subscribe" : "unsubscribe"} ${count === 1 ? "this company" : "these companies"}`,
				description: "Try again.",
				icon,
			})
		} finally {
			setPendingSubscription(null)
		}
	}, [selectedIds, subscriptionTarget, zero, clearSelection])

	return (
		<>
			<MetaInfo title="Customers" />
			<AlertDialog
				isOpen={isConfirmDeleteOpen}
				onOpenChange={setIsConfirmDeleteOpen}
				confirmText="Delete"
				title={`Delete ${selectedCount} ${selectedCount === 1 ? "company" : "companies"}?`}
				description="Their contacts and threads stay. Their Linear customers are deleted too."
				onCancel={() => setIsConfirmDeleteOpen(false)}
				onConfirm={deleteSelected}
			/>
			<AlertDialog
				isOpen={isConfirmSubscriptionOpen}
				onOpenChange={setIsConfirmSubscriptionOpen}
				variant="positive"
				confirmText={subscriptionTarget ? "Subscribe" : "Unsubscribe"}
				title={`${subscriptionTarget ? "Subscribe" : "Unsubscribe"} everyone at ${selectedCount} ${selectedCount === 1 ? "company" : "companies"}?`}
				description={
					subscriptionTarget
						? "Everyone at these companies gets update emails again. Blocked senders stay unsubscribed."
						: "Everyone at these companies stops getting update emails. Support replies still go out."
				}
				onCancel={() => setIsConfirmSubscriptionOpen(false)}
				onConfirm={setSelectedSubscribed}
			/>
			<RoleCheck not={["VIEWER"]}>
				<SelectionPill
					count={selectedCount}
					noun={["company", "companies"]}
					onClear={clearSelection}
					actions={[
						{
							label: "Subscribe",
							onAction: () => openSubscriptionDialog(true),
							isLoading: pendingSubscription === true,
						},
						{
							label: "Unsubscribe",
							onAction: () => openSubscriptionDialog(false),
							isLoading: pendingSubscription === false,
							shortcut: { keys: "u", hint: "U" },
						},
						{
							label: "Delete",
							onAction: () => setIsConfirmDeleteOpen(true),
							isLoading: isDeleting,
							shortcut: {
								keys: "meta+backspace, ctrl+backspace",
								hint: `${ShortcutSymbols.CMD}${ShortcutSymbols.BACKSPACE}`,
							},
						},
					]}
				/>
			</RoleCheck>
			<div className="sticky top-0 z-1 bg-white dark:bg-gray-900">
				<CustomersPageHeader
					actions={
						<div className="flex flex-wrap items-center gap-2">
							<CompaniesFilter />
							<RoleCheck not={["VIEWER"]}>
								<Tooltip label="Import customers">
									<IconButton
										variant="ghost"
										Icon={CloudUpload}
										aria-label="Import customers"
										onClick={() =>
											void navigate({ to: "/settings/contacts-csv-import" })
										}
									/>
								</Tooltip>
							</RoleCheck>

							<CreateCustomerDialog
								isOpen={isCompanyOpen}
								onOpenChange={(value) => {
									setIsCompanyOpen(value)
									if (!value) {
										analytics.track("close-create-company-dialog")
									}
								}}
							/>
							<RoleCheck not={["VIEWER"]}>
								<Tooltip
									label="Connect Linear to create customer"
									enabled={!hasLinearConnection(workspace)}
									shouldWrapChildren
								>
									<Button
										disabled={!hasLinearConnection(workspace)}
										Icon={PlusIcon}
										variant="ghost"
										onClick={() => {
											setIsCompanyOpen(true)
											analytics.track("open-create-company-dialog")
										}}
									>
										Create
									</Button>
								</Tooltip>
							</RoleCheck>
						</div>
					}
				/>

				<div className="flex items-center gap-2 px-6 py-2">
					<Search className="h-4 w-4 flex-none text-secondary" />
					<Input
						variant="borderless"
						textSize="sm"
						placeholder="Find by name or domain..."
						value={search}
						name="customer-search"
						className="px-0 placeholder:text-tertiary"
						inputClassName="h-7"
						onChange={(e) => {
							setSearch(e.target.value)
						}}
					/>
				</div>

				<CompaniesFilterControl />

				{hasCompanies && (
					<div className="flex items-center justify-between border-b border-gray-200 px-6 py-2 text-xs text-secondary dark:border-gray-700/80">
						<div className="flex w-64 flex-none items-center gap-3">
							<RoleCheck not={["VIEWER"]}>
								<SelectionCheckbox
									label={
										allLoadedSelected
											? "Clear selection"
											: "Select loaded companies"
									}
									checked={
										allLoadedSelected
											? true
											: selectedCount > 0
												? "indeterminate"
												: false
									}
									onCheckedChange={toggleAllLoaded}
								/>
							</RoleCheck>
							<SortableTableHeader
								label="Name"
								currentSortType={sortType}
								sortTypeAsc="name-asc"
								sortTypeDesc="name-desc"
								onClick={setSortType}
							/>
						</div>
						<div className="flex items-center gap-4">
							<div className="w-20 flex-none">
								<NonSortableTableHeader
									label="Contacts"
									className="flex w-full justify-end"
								/>
							</div>
							<div className="w-20 flex-none">
								<SortableTableHeader
									label={
										workspace?.revenueFormatting === "ANNUAL" ? "ARR" : "MRR"
									}
									currentSortType={sortType}
									sortTypeAsc="mrr-asc"
									sortTypeDesc="mrr-desc"
									onClick={setSortType}
									arrowPosition="left"
								/>
							</div>
							<div className="w-16 flex-none">
								<SortableTableHeader
									label="Size"
									currentSortType={sortType}
									sortTypeAsc="size-asc"
									sortTypeDesc="size-desc"
									onClick={setSortType}
									arrowPosition="left"
								/>
							</div>
							<div className="w-24 flex-none">
								<SortableTableHeader
									label="Tier"
									currentSortType={sortType}
									sortTypeAsc="tier-asc"
									sortTypeDesc="tier-desc"
									onClick={setSortType}
									arrowPosition="left"
								/>
							</div>
							<div className="w-24 flex-none pl-8">
								<SortableTableHeader
									label="Stage"
									currentSortType={sortType}
									sortTypeAsc="stage-asc"
									sortTypeDesc="stage-desc"
									onClick={setSortType}
								/>
							</div>
							<div className="w-24 flex-none">
								<SortableTableHeader
									label="Created"
									currentSortType={sortType}
									sortTypeAsc="oldest"
									sortTypeDesc="newest"
									onClick={setSortType}
									arrowPosition="left"
								/>
							</div>
							<div className="w-12 flex-none">
								<NonSortableTableHeader
									label="Owner"
									className="flex w-full justify-end"
								/>
							</div>
						</div>
					</div>
				)}
			</div>

			{hasCompanies ? (
				<ScrollArea className="min-h-0 flex-1">
					<ScrollViewport
						ref={scrollportRef}
						className="h-full pb-2 text-sm sm:pb-20"
					>
						<div
							className="relative w-full"
							style={{ height: virt.virtualizer.getTotalSize() }}
							onMouseLeave={rows.onListMouseLeave}
						>
							{virtualItems.map((virtualItem) => {
								const company = virt.rowAt(virtualItem.index) as Company & {
									companyMeta?: { brandfetchData?: unknown }
								}
								if (!company) return null
								return (
									<Link
										key={virtualItem.key}
										to="/customers/$id"
										params={{ id: company.id }}
										data-index={virtualItem.index}
										onClick={(event) =>
											rows.onRowClick(virtualItem.index, event)
										}
										onMouseMove={() => rows.onRowMouseMove(virtualItem.index)}
										className={cn(
											"absolute flex w-full select-none items-center justify-between px-6 py-2.5",
											rowHighlightClass({
												isCursor: rows.cursor === virtualItem.index,
												isChecked: selectedIds.has(company.id),
											}),
										)}
										style={{
											transform: `translateY(${virtualItem.start}px)`,
										}}
									>
										<div className="flex w-72 flex-none items-center gap-3">
											<RoleCheck not={["VIEWER"]}>
												<SelectionCheckbox
													label={`Select ${company.name || company.domain?.[0] || "company"}`}
													checked={selectedIds.has(company.id)}
													onCheckedChange={(checked) =>
														rows.checkAt(virtualItem.index, checked === true)
													}
													onShiftClick={() =>
														rows.selectRange(virtualItem.index)
													}
												/>
											</RoleCheck>
											<CompanyLogo
												size="s"
												brandfetchData={
													company.companyMeta?.brandfetchData as BrandfetchData
												}
												fallback={company.name}
												linearLogoUrl={company?.logoUrl || undefined}
												className="mr-1"
											/>
											<div className="min-w-0 truncate text-sm font-medium leading-tight">
												{company.name || company.domain?.[0]}
											</div>
										</div>
										<div className="flex items-center gap-4">
											<div className="w-20 flex-none text-right tabular-nums">
												{contactCounts[company.id]}
											</div>
											<div className="w-20 flex-none text-right">
												{company?.revenue === null ||
												company?.revenue === undefined
													? ""
													: formatRevenue({
															revenue: company?.revenue,
															revenueFormatting:
																workspace?.revenueFormatting || "MONTHLY",
															currency: workspace?.revenueCurrency,
														})}
											</div>
											<div className="w-16 flex-none text-right">
												{company?.size === null || company?.size === undefined
													? ""
													: company.size}
											</div>
											<div className="w-24 flex-none text-right">
												{!!company?.tierName && company.tierName}
											</div>
											<div className="w-24 flex-none pl-8 text-left">
												{hasLinearConnection(workspace) && (
													<div className="flex items-center">
														<div
															style={{
																backgroundColor:
																	company.statusColor ?? defaultStatus.color,
															}}
															className="mr-2 h-2 w-2 flex-none"
														/>
														<span className="truncate">
															{company.statusName ?? defaultStatus.name}
														</span>
													</div>
												)}
											</div>
											<div className="w-24 flex-none text-right text-tertiary">
												{format(new Date(company.createdAt), "MMM d, yyyy")}
											</div>
											<div className="flex w-12 flex-none items-center justify-end">
												{company.linearOwnerAvatarUrl ? (
													<img
														src={company.linearOwnerAvatarUrl}
														alt={company.linearOwnerName ?? "Owner"}
														title={company.linearOwnerName ?? "Owner"}
														className="h-5 w-5 rounded-full object-cover"
													/>
												) : (
													<Owner />
												)}
											</div>
										</div>
									</Link>
								)
							})}
						</div>
					</ScrollViewport>
				</ScrollArea>
			) : (
				<div className="flex min-h-0 flex-1 justify-center">
					<Empty
						text={
							debouncedSearch.trim()
								? "Try changing the search parameters"
								: "Customers are generated from emails and will be pushed to Linear as soon as you link an issue or project."
						}
						title={debouncedSearch.trim() ? "Nothing was found" : "Customers"}
						image={
							<img
								width={130}
								height={128}
								src={EmptyCompany}
								alt="Empty customers"
							/>
						}
					/>
				</div>
			)}
		</>
	)
}
